import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";

import { useDialogStyles } from './styles';
import StatusSelect from './StatusSelect';
import ClientInfo from './ClientInfo';
import EventInfo from './EventInfo';
import NoteSection from './NoteSection';
import DevisForm from './DevisForm';
import DevisExisting from './DevisExisting';

const apiUrl = process.env.REACT_APP_API_URL;

export default function DevisDialog({ visible, onHide, leadData, onDevisGenerated, forceNew = false,onResetForceNew }) {
  // Styles
  useDialogStyles();


const [isForcedNew, setIsForcedNew] = useState(forceNew);

useEffect(() => {
  setIsForcedNew(forceNew);
}, [forceNew]);


  // États du formulaire devis
  const [formule, setFormule] = useState("avec_alcool");
  const [prixParPersonne, setPrixParPersonne] = useState(20);
  const [remise, setRemise] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [nbPersonnes, setNbPersonnes] = useState(leadData?.nbpers || 0);
  const [nbBarmen, setNbBarmen] = useState(1);
  const [structure, setStructure] = useState("tuk_tuk");
  const [options, setOptions] = useState([]);

  // Textes personnalisés du devis
  const [customPackName, setCustomPackName] = useState("");
  const [customPackDescription, setCustomPackDescription] = useState("");
  const [customFormuleName, setCustomFormuleName] = useState("");
  const [customFormuleDescription, setCustomFormuleDescription] = useState("");

  // États du dialog
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [devisInfo, setDevisInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // États des champs client
  const [tempNom, setTempNom] = useState(leadData?.nomPrenom || "");
  const [tempEmail, setTempEmail] = useState(leadData?.email || "");
  const [tempTel, setTempTel] = useState(leadData?.tel || "");
  const [tempLieu, setTempLieu] = useState(leadData?.lieu || "");
  const [tempNbpers, setTempNbpers] = useState(leadData?.nbpers || "");
  const [tempDateEvent, setTempDateEvent] = useState(leadData?.dateEvent || "");
  const [tempTypeBar, setTempTypeBar] = useState(leadData?.type_bar || "");
  const [tempMore, setTempMore] = useState(leadData?.plusInfos || "");
  const [tempDateUpdate, setTempDateUpdate] = useState("");

  // États des notes
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteHistoryRefreshKey, setNoteHistoryRefreshKey] = useState(0);

  // États des accordéons
  const [openClientInfo, setOpenClientInfo] = useState(true);
  const [openEventInfo, setOpenEventInfo] = useState(false);
  const [openNote, setOpenNote] = useState(false);

  // Calculs
  const sousTotalPrestation = nbPersonnes * prixParPersonne;
  const sousTotalOptions = options.reduce((sum, opt) => sum + (opt.prix || 0), 0);
  const sousTotal = sousTotalPrestation + sousTotalOptions;
  const montantRemise = (sousTotal * remise) / 100;
  const total = sousTotal - montantRemise;

  // Effets
  useEffect(() => {
    if (leadData) {
      setTempNom(leadData.nomPrenom || "");
      setTempEmail(leadData.email || "");
      setTempTel(leadData.tel || "");
      setTempLieu(leadData.lieu || "");
      setTempNbpers(leadData.nbpers || "");
      setTempDateEvent(leadData.dateEvent || "");
      setTempTypeBar(leadData.type_bar || "");
      setTempMore(leadData.plusInfos || "");
      setHasChanges(false);

      if (leadData.etat !== undefined && leadData.etat !== null) {
        setSelectedStatus(String(leadData.etat));
      } else {
        setSelectedStatus("");
      }

      if (leadData?.dateUpdate) {
        setTempDateUpdate(new Date(leadData.dateUpdate).toLocaleDateString("fr-FR"));
      }
    }
  }, [leadData]);

  useEffect(() => {
    if (leadData) {
      const hasAnyChange =
        tempNom !== (leadData.nomPrenom || "") ||
        tempEmail !== (leadData.email || "") ||
        tempTel !== (leadData.tel || "") ||
        tempLieu !== (leadData.lieu || "") ||
        tempNbpers !== (leadData.nbpers || "") ||
        tempDateEvent !== (leadData.dateEvent || "") ||
        tempTypeBar !== (leadData.type_bar || "") ||
        tempMore !== (leadData.plusInfos || "");

      setHasChanges(hasAnyChange);
    }
  }, [tempNom, tempEmail, tempTel, tempLieu, tempNbpers, tempDateEvent, tempTypeBar, tempMore, leadData]);

  useEffect(() => {
    if (leadData?.nbpers) setNbPersonnes(leadData.nbpers);
  }, [leadData]);

  useEffect(() => {
    if (visible && leadData?.idLead && !forceNew) {
      checkExistingDevis();
    } else if (visible && forceNew) {
      setDevisInfo(null);
      setLoading(false);
    }
  }, [visible, leadData, forceNew]);

  useEffect(() => {
    if (forceNew) {
      resetForm();
    }
  }, [forceNew]);

  // Fonctions
  const checkExistingDevis = async () => {
    setLoading(true);
    setDevisInfo(null);

    if (forceNew) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${apiUrl}/IHM/api/generer_devis.php?lead_id=${leadData.idLead}`);
      if (response.data.a_devis == 1) {
        setDevisInfo({
          a_devis: response.data.a_devis,
          numero_devis: response.data.dernier_devis_id,
          lien_devis: response.data.lien_devis,
          lien_pdf: response.data.lien_pdf,
          token_devis: response.data.token_devis,
          montant_devis: response.data.montant_total || 0,
          date_devis: response.data.date_devis || new Date().toISOString(),
          idVosFactures: response.data.idVosFactures,
          statut_devis: response.data.statut_devis || "brouillon",
        });
      } else {
        setDevisInfo(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormule("avec_alcool");
    setPrixParPersonne(20);
    setRemise(0);
    setNbPersonnes(leadData?.nbpers || 0);
    setNbBarmen(1);
    setStructure("tuk_tuk");
    setOptions([]);
    setCustomPackName("");
    setCustomPackDescription("");
    setCustomFormuleName("");
    setCustomFormuleDescription("");
  };

  const handleSaveClient = async () => {
    try {
      await axios.post(`${apiUrl}/IHM/api/update_ficheLead.php`, {
        idLead: leadData.idLead,
        nom: tempNom,
        telephone: tempTel,
        email: tempEmail,
        lieu: tempLieu,
        nbpers: tempNbpers,
        typeBar: tempTypeBar,
        more: tempMore,
        dateEvent: tempDateEvent,
        adminMod: localStorage.getItem("us"),
      });

      setHasChanges(false);
      setSnackbarMessage("Informations client mises à jour");
      setOpenSnackbar(true);
      if (onDevisGenerated) onDevisGenerated();
    } catch (error) {
      console.error("Erreur de mise à jour :", error);
      setSnackbarMessage("Erreur lors de la sauvegarde");
      setOpenSnackbar(true);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);

    if (!newStatus) return;

    try {
      const data = {
        idLead: leadData.idLead,
        etat: newStatus,
        note: '',
        adminMod: localStorage.getItem("us"),
      };

      const response = await axios.put(`${apiUrl}/IHM/api/lead.php`, data);
      if (response.data) {
        setSnackbarMessage(`État changé avec succès`);
        setOpenSnackbar(true);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setSnackbarMessage("Erreur lors du changement d'état");
      setOpenSnackbar(true);
      setSelectedStatus(leadData?.etat || "");
    }
  };

  const handleSaveNote = async () => {
    if (!leadData || !note.trim()) return;
    setSavingNote(true);
    try {
      const response = await axios.put(`${apiUrl}/IHM/api/lead.php`, {
        idLead: leadData.idLead,
        note: note,
        adminMod: localStorage.getItem("us"),
        noteOnly: true,
      });

      if (response.data) {
        setSnackbarMessage("Note enregistrée");
        setOpenSnackbar(true);
        setNote("");
        setNoteHistoryRefreshKey((k) => k + 1);
        if (onDevisGenerated) onDevisGenerated();
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la note :", error);
      setSnackbarMessage("Erreur lors de l'enregistrement de la note");
      setOpenSnackbar(true);
    } finally {
      setSavingNote(false);
    }
  };

  const handleGenererDevis = async () => {
    if (!leadData) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${apiUrl}/IHM/api/generer_devis.php`, {
        lead_id: leadData.idLead,
        nom: leadData.nomPrenom,
        email: leadData.email,
        tel: leadData.tel,
        lieu: leadData.lieu,
        nb_personnes: nbPersonnes,
        nb_barmen: nbBarmen,
        formule: formule,
        prix_par_personne: prixParPersonne,
        remise: remise,
        structure: structure,
        options: options,
        custom_pack_name: customPackName,
        custom_pack_description: customPackDescription,
        custom_formule_name: customFormuleName,
        custom_formule_description: customFormuleDescription,
        is_new_version: forceNew,
        adminMod: localStorage.getItem("us"),
      });

      if (response.data.success) {
        setDevisInfo({
          a_devis: 1,
          numero_devis: response.data.devis_number,
          lien_devis: response.data.devis_url,
          lien_pdf: response.data.pdf_url,
          token_devis: response.data.token_devis,
          montant_devis: response.data.montant,
          date_devis: new Date().toISOString(),
          statut_devis: "brouillon",
        });
     
setIsForcedNew(false);
if (onResetForceNew) onResetForceNew();


        setSnackbarMessage("Devis généré avec succès !");
        setOpenSnackbar(true);
        if (onDevisGenerated) onDevisGenerated();
      } else {
        setSnackbarMessage(response.data.message || "Erreur");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Erreur lors de la génération");
      setOpenSnackbar(true);
    } finally {
      setGenerating(false);
    }
  };

  const ajouterOption = () => {
    setOptions([...options, { titre: "", description: "", prix: 0 }]);
  };

  const supprimerOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const modifierOption = (index, champ, valeur) => {
    const newOptions = [...options];
    newOptions[index][champ] = valeur;
    setOptions(newOptions);
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const header = (
    <div className="flex justify-content-between align-items-center w-full">
      <span className="font-semibold text-lg" style={{ color: "#322915" }}>
        Fiche client & devis
      </span>
    </div>
  );

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseSnackbar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Dialog
        blockScroll
        visible={visible}
        header={header}
        className="custom-dialog"
        style={{ width: "48vw", maxWidth: "720px" }}
        modal
        onHide={() => {
          onHide();
          resetForm();
          setOpenClientInfo(true);
          setOpenEventInfo(false);
          setOpenNote(false);
        }}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
      >
        <div>
          <StatusSelect
            selectedStatus={selectedStatus}
            onStatusChange={handleStatusChange}
            dateUpdate={tempDateUpdate}
          />

          <ClientInfo
            isOpen={openClientInfo}
            onToggle={() => setOpenClientInfo(!openClientInfo)}
            tempNom={tempNom}
            setTempNom={setTempNom}
            tempEmail={tempEmail}
            setTempEmail={setTempEmail}
            tempTel={tempTel}
            setTempTel={setTempTel}
            hasChanges={hasChanges}
            onSave={handleSaveClient}
          />

          <EventInfo
            isOpen={openEventInfo}
            onToggle={() => setOpenEventInfo(!openEventInfo)}
            tempNbpers={tempNbpers}
            setTempNbpers={setTempNbpers}
            tempLieu={tempLieu}
            setTempLieu={setTempLieu}
            tempDateEvent={tempDateEvent}
            setTempDateEvent={setTempDateEvent}
            tempTypeBar={tempTypeBar}
            setTempTypeBar={setTempTypeBar}
            tempMore={tempMore}
            setTempMore={setTempMore}
            hasChanges={hasChanges}
            onSave={handleSaveClient}
          />

          <NoteSection
            isOpen={openNote}
            onToggle={() => setOpenNote(!openNote)}
            note={note}
            setNote={setNote}
            savingNote={savingNote}
            onSaveNote={handleSaveNote}
            idLead={leadData?.idLead}
            refreshKey={noteHistoryRefreshKey}
          />

          <div className="static-section-title">
            <i className="pi pi-file"></i>
            {devisInfo?.a_devis && !forceNew ? "Devis actuel" : "Génération du devis"}
          </div>

          {loading ? (
            <div className="flex justify-content-center p-3">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: "1.2rem", color: "#322915" }}></i>
            </div>
          ) : !devisInfo?.a_devis || forceNew ? (
            <DevisForm
              nbPersonnes={nbPersonnes}
              setNbPersonnes={setNbPersonnes}
              nbBarmen={nbBarmen}
              setNbBarmen={setNbBarmen}
              structure={structure}
              setStructure={setStructure}
              formule={formule}
              setFormule={setFormule}
              prixParPersonne={prixParPersonne}
              setPrixParPersonne={setPrixParPersonne}
              remise={remise}
              setRemise={setRemise}
              options={options}
              onAddOption={ajouterOption}
              onRemoveOption={supprimerOption}
              onModifyOption={modifierOption}
              sousTotalPrestation={sousTotalPrestation}
              sousTotalOptions={sousTotalOptions}
              montantRemise={montantRemise}
              total={total}
              generating={generating}
              onGenerate={handleGenererDevis}
              disabled={generating || nbPersonnes === 0}
              customPackName={customPackName}
              setCustomPackName={setCustomPackName}
              customPackDescription={customPackDescription}
              setCustomPackDescription={setCustomPackDescription}
              customFormuleName={customFormuleName}
              setCustomFormuleName={setCustomFormuleName}
              customFormuleDescription={customFormuleDescription}
              setCustomFormuleDescription={setCustomFormuleDescription}
            />
          ) : (
            <DevisExisting
              devisInfo={devisInfo}
              leadData={leadData}
              onSnackbarMessage={setSnackbarMessage}
              onOpenSnackbar={setOpenSnackbar}
            />
          )}
        </div>
      </Dialog>
    </>
  );
}